<template>
    <div
        class="calendar"
        v-if="loaded">
        <FullCalendar
            ref="fullCalendar"
            :options="calendarOptions"></FullCalendar>
    </div>
</template>

<script>
import listPlugin from '@fullcalendar/list';
import dayGridPlugin from '@fullcalendar/daygrid';
import FullCalendarLocale from '@fullcalendar/core/locales/nl';
import FullCalendar from '@fullcalendar/vue';
import moment from 'moment';

export default {
    name: 'design-2021-apprentice-fullcalendar-component',
    components: {
        FullCalendar,
    },
    props: {
        calendarItemsIn: Array,
        types: Array,
        leftButtons: {
            type: Boolean,
            default: true,
        },
        rightButtons: {
            type: Boolean,
            default: false,
        },
        defaultGrid: {
            type: String,
            default: 'listYear',
        },
        emitChangedDate: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            calendarOptions: null,
            loaded: false,
        };
    },
    watch: {
        calendarItemsIn: function (items) {
            this.calendarOptions.events = items.map((item) => {
                if (item.roadmap) {
                    item.allDay = true;
                } else {
                    item.allDay =
                        moment(item.date).isSame(moment(item.date_end)) && item.time === null && item.time_end === null;
                }

                item.color = item.color;
                return item;
            });
        },
    },

    mounted() {
        const self = this;
        let events = [];

        if (this.calendarItemsIn.length > 0) {
            events = this.calendarItemsIn.map((item) => {
                if (item.roadmap) {
                    item.allDay = true;
                } else {
                    item.allDay =
                        moment(item.date).isSame(moment(item.date_end)) && item.time === null && item.time_end === null;
                }

                return item;
            });
        }

        if (!self.loaded) {
            self.calendarOptions = {
                plugins: [listPlugin, dayGridPlugin],
                initialView: self.defaultGrid,
                contentHeight: 'auto',
                headerToolbar: {
                    center: 'title',
                    left: self.leftButtons ? 'today,prev,next' : '',
                    end: self.rightButtons ? self.types.join() : '',
                },
                locale: FullCalendarLocale,
                weekNumbers: true,
                events,
                eventContent: function (arg) {
                    let customEl = '';
                    if (arg.event.extendedProps.isDeadline) {
                        const link =
                            window.location.origin +
                            '/assignment/' +
                            arg.event.extendedProps.assignment_id +
                            '/' +
                            arg.event.extendedProps.project_id;
                        customEl = document.createElement('a');
                        customEl.setAttribute('href', link);
                        customEl.setAttribute('class', 'deadline-title-link');
                        customEl.addEventListener('click', function (e) {
                            e.stopPropagation();
                        });
                        customEl.innerHTML = arg.event._def.title;
                    } else {
                        customEl = document.createElement('span');
                        customEl.setAttribute('class', 'calendar-item-title');
                        customEl.innerHTML = arg.event._def.title;
                    }
                    let arrayOfDomNodes = [customEl];

                    return { domNodes: arrayOfDomNodes };
                },
                eventClick: function (item) {
                    self.$emit('update-item', item.event);
                },
                eventTimeFormat: {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false,
                },
                datesSet: function () {
                    let calendarApi = self.$refs.fullCalendar.getApi();
                    if (self.emitChangedDate) {
                        let newDate = calendarApi.getDate();
                        let calenderView = calendarApi.view.type;

                        let data = {
                            date: newDate,
                            view: calenderView,
                        };
                        self.$emit('change-date', data);
                    }
                },
            };

            self.loaded = true;
        }
    },
};
</script>

<style scoped lang="scss">
.calendar::v-deep {
    .fc-header-toolbar {
        flex-direction: column;

        .fc-toolbar-chunk:first-child {
            width: 100%;
            margin-top: map-get($grid-gutter-spacing, 'fifteen');
            margin-bottom: map-get($grid-gutter-spacing, 'fifteen');

            .fc-button-group {
                display: flex;
                flex: 1 0 100%;
                float: none;

                button {
                    flex: 0 0 auto;

                    &:nth-child(2) {
                        margin-left: auto;
                    }
                }
            }

            button {
                &,
                &:active,
                &:focus,
                &:active:focus,
                &:not(:disabled).fc-button-active {
                    box-shadow: none;
                    color: $white;
                    background-color: var(--primary) !important;
                    border: none;
                }
            }
        }

        .fc-toolbar-chunk:last-child {
            align-self: flex-end;
        }
    }

    .fc-list-empty {
        background: none;
        align-items: flex-start;
    }

    .fc .fc-list-event .fc-list-event-time {
        width: 85px;
    }

    .fc-list,
    .fc-daygrid {
        border: none;
    }

    .fc-today-button {
        background-color: var(--primary);
        border-color: transparent;
        padding: map-get($grid-gutter-spacing, 'eight');
        height: auto;
    }

    .fc-listWeek-button,
    .fc-listYear-button,
    .fc-dayGridMonth-button,
    .fc-dayGridWeek-button {
        border-color: transparent;
        padding: map-get($grid-gutter-spacing, 'eight');
        height: auto;
    }

    .fc-dayGridMonth-button,
    .fc-dayGridWeek-button,
    .fc-listWeek-button,
    .fc-listYear-button {
        box-shadow: none;
        border: none;

        &:after {
            display: block;
            content: '';
            width: 21px;
            height: 5px;
            margin-top: map-get($grid-gutter-spacing, 'fifteen');
            position: relative;
            opacity: 0;
            transition: all 0.3s ease-in-out;
            left: -35px;
        }

        &:hover {
            &:after {
                background-color: var(--primary);
                left: calc(50% - 10.5px);
                opacity: 1;
            }
        }

        &:active,
        &:focus,
        &:active:focus,
        &:not(:disabled).fc-button-active {
            box-shadow: none;
            color: var(--primary);
            border: none;
        }

        &.fc-button-active {
            color: var(--primary);
            box-shadow: none;

            &:after {
                background-color: var(--primary);
                left: calc(50% - 10.5px);
                opacity: 1;
            }

            &:focus {
                box-shadow: none;
            }
        }
    }

    .fc-listWeek-button,
    .fc-listYear-button,
    .fc-dayGridMonth-button,
    .fc-dayGridWeek-button {
        margin-left: map-get($grid-gutter-spacing, 'eight') !important;
        background-color: transparent;
        border-color: transparent;
        border: none;
        color: var(--primary);
        font-weight: $font-weight-medium;
        transition:
            color 0.15s ease-in-out,
            background-color 0.15s ease-in-out,
            border-color 0.15s ease-in-out,
            box-shadow 0.15s ease-in-out;
    }

    .fc-prev-button,
    .fc-next-button {
        background-color: var(--primary);
        border-color: transparent;
        padding: map-get($grid-gutter-spacing, 'eight');
        height: auto;
        margin-left: map-get($grid-gutter-spacing, 'eight') !important;
    }

    .fc .fc-button-primary:not(:disabled):active,
    .fc .fc-button-primary:not(:disabled).fc-button-active {
        background: transparent;
        border-color: transparent;
    }

    .fc-event,
    .fc .fc-list-event {
        background-color: $secondary-background-color;
        border-color: transparent;
        cursor: pointer;
        white-space: break-spaces;

        &:hover {
            background-color: var(--primary);

            td {
                color: $white;
            }

            .fc-list-event-title {
                .deadline-title-link {
                    color: $white;
                }
            }
        }
    }

    .fc-list-event,
    .fc-h-event {
        background-color: $secondary-background-color;
        border-color: transparent;
        padding: map-get($grid-gutter-spacing, 'fifteen') map-get($grid-gutter-spacing, 'eight');
    }

    .fc-list-day-cushion {
        background-color: $white;
        border-color: transparent;
    }

    .fc-theme-standard td,
    .fc-theme-standard th,
    .fc-theme-standard .fc-scrollgrid {
        border-color: transparent;
    }

    .fc .fc-list-table td,
    .fc .fc-list-day-cushion {
        padding: 0;
        margin-bottom: map-get($grid-gutter-spacing, 'fifteen');
        margin-top: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .fc .fc-list-day-cushion {
        border-bottom: 3px dotted $border-color;
        padding-bottom: map-get($grid-gutter-spacing, 'eight');
    }

    a.fc-col-header-cell-cushion,
    .fc-daygrid-day-number {
        color: $black;
    }

    .fc-daygrid-event {
        padding: map-get($grid-gutter-spacing, 'five');
    }

    .fc-list-day-text {
        color: $black;
    }

    .fc-list-day-side-text {
        color: $text-muted-color;
    }

    .fc-list-event-time {
        color: $black;
        margin-right: map-get($grid-gutter-spacing, 'fifteen');
    }

    .fc-list-event-title {
        color: $black;

        .deadline-title-link {
            color: var(--primary);
            font-weight: $font-weight-bold;
        }
    }

    .fc .fc-list-event-dot {
        border-color: 5px solid var(--primary);
        margin-right: map-get($grid-gutter-spacing, 'eight');
        margin-left: map-get($grid-gutter-spacing, 'twenty-one');
    }

    .fc-event-title {
        color: $black;
    }

    .fc-daygrid-event {
        background-color: var(--primary);
    }

    .fc-daygrid-event .fc-even-title {
        color: $white;
    }

    .fc-listYear-view {
        .fc-list-table {
            .fc-list-event {
                td {
                    background-color: transparent;
                }
            }
        }
    }

    .fc-dayGridWeek-view,
    .fc-dayGridMonth-view {
        .fc-day-today {
            background-color: $secondary-background-color;
        }

        a.fc-daygrid-event {
            background-color: transparent;
            color: $text-color;

            .deadline-title-link {
                font-weight: $font-weight-bold;
            }

            &:hover {
                background: var(--primary);

                &,
                .deadline-title-link {
                    color: $white;
                }
            }
        }
    }

    .fc .fc-daygrid-week-number {
        background-color: var(--primary);
        color: $white;
        border-radius: 8px;
        padding: 5px;
        font-size: 0.75rem;
    }
}

@media (min-width: map-get($grid-breakpoints, 'md')) {
    .calendar::v-deep {
        .fc-header-toolbar {
            flex-direction: row;

            .fc-toolbar-chunk:first-child {
                width: auto;
                margin-top: 0;
                margin-bottom: 0;
            }

            .fc-toolbar-chunk:last-child {
                align-self: flex-end;
            }
        }
    }
}
</style>
